import { APP_PREFIX } from '../config/appIdentifiers';

const STORAGE_KEY = `${APP_PREFIX}_cookie_preferences_v1`;
const CONSENT_VERSION = 1;

export const COOKIE_CONSENT_CHANGED_EVENT = `${APP_PREFIX}:cookie-consent-changed`;
export const COOKIE_MANAGER_OPEN_EVENT = `${APP_PREFIX}:cookie-manager-open`;

const DEFAULT_PREFERENCES = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false
});

const normalizePreferences = (preferences = {}) => ({
  necessary: true,
  analytics: preferences.analytics === true,
  marketing: preferences.marketing === true
});

const getDefaultConsentState = () => ({
  hasConsented: false,
  version: CONSENT_VERSION,
  updatedAt: null,
  preferences: { ...DEFAULT_PREFERENCES }
});

const buildConsentState = (storedConsent) => {
  if (!storedConsent) {
    return getDefaultConsentState();
  }

  return {
    hasConsented: true,
    version: Number(storedConsent.version) || CONSENT_VERSION,
    updatedAt: typeof storedConsent.updatedAt === 'string' ? storedConsent.updatedAt : null,
    preferences: normalizePreferences(storedConsent.preferences)
  };
};

const readStoredConsent = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const dispatchConsentChanged = (consent) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: consent }));
};

const persistConsent = (preferences) => {
  const normalizedPreferences = normalizePreferences(preferences);
  const payload = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    preferences: normalizedPreferences
  };

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore storage errors (private mode, quota, etc.).
    }
  }

  const consentState = buildConsentState(payload);
  dispatchConsentChanged(consentState);
  return consentState;
};

const cookieConsentService = {
  STORAGE_KEY,
  getConsent() {
    return buildConsentState(readStoredConsent());
  },
  saveConsent(preferences) {
    return persistConsent(preferences);
  },
  acceptAll() {
    return persistConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  },
  rejectOptional() {
    return persistConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  },
  canUseCategory(category) {
    if (category === 'necessary') return true;
    const consent = this.getConsent();
    return consent.preferences[category] === true;
  },
  openManager() {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    window.dispatchEvent(new Event(COOKIE_MANAGER_OPEN_EVENT));
  }
};

export const openCookieManager = () => {
  cookieConsentService.openManager();
};

export default cookieConsentService;
